/**
 * Announcement bar rotation: one announcement at a time, fading to the next.
 *
 * Loaded only by a bar with more than one announcement and rotation switched
 * on. There are no controls, so anything that would leave an announcement
 * unreachable turns the rotation off and leaves the bar as it renders without
 * this script — every announcement on one line:
 *   - prefers-reduced-motion, at load or switched on later;
 *   - fewer than two announcements displayed at this width. A "desktop only"
 *     announcement is `display: none` below 1024px, so a phone may have just
 *     one left. Worked out again whenever 1024px is crossed.
 *
 * It pauses while the pointer is over the bar, while keyboard focus is inside
 * it (so a linked announcement cannot vanish from under a Tab), and while the
 * tab is hidden. There is no aria-live region: reading out every change would
 * talk over the page. A screen reader gets the announcement that is showing.
 */
(function () {
  'use strict';

  if (!window.Theme) return;

  const REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
  const DESKTOP = '(min-width: 1024px)';

  class AnnouncementRotator extends window.Theme.ThemeElement {
    init() {
      this.all = this.$$('.announcement');
      if (this.all.length < 2) return;

      this.interval = Number(this.dataset.interval) || 5000;
      this.reduced = window.matchMedia(REDUCED_MOTION);
      this.desktop = window.matchMedia(DESKTOP);
      this.timer = null;
      this.hovered = false;
      this.focused = false;
      this.index = 0;

      this.on(this, 'pointerenter', () => {
        this.hovered = true;
        this.stopTimer();
      });
      this.on(this, 'pointerleave', () => {
        this.hovered = false;
        this.startTimer();
      });
      this.on(this, 'focusin', () => {
        this.focused = true;
        this.stopTimer();
      });
      this.on(this, 'focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focused = false;
        this.startTimer();
      });
      this.on(document, 'visibilitychange', () => {
        if (document.hidden) this.stopTimer();
        else this.startTimer();
      });

      if (typeof this.desktop.addEventListener === 'function') {
        this.on(this.desktop, 'change', () => this.refresh());
      }
      if (typeof this.reduced.addEventListener === 'function') {
        this.on(this.reduced, 'change', () => this.refresh());
      }

      this.refresh();
    }

    destroy() {
      this.stopTimer();
    }

    /** Decide whether to rotate at all, and over which announcements. */
    refresh() {
      const current = this.slides ? this.slides[this.index] : null;
      this.slides = this.all.filter((slide) => getComputedStyle(slide).display !== 'none');
      const kept = this.slides.indexOf(current);
      this.index = kept === -1 ? 0 : kept;

      if (this.reduced.matches || this.slides.length < 2) {
        this.stopTimer();
        delete this.dataset.ready;
        this.all.forEach((slide) => {
          slide.classList.remove('is-current');
          slide.removeAttribute('inert');
        });
        return;
      }

      // Hands "what shows" from the one-line layout to the rotation. Set in
      // the same task as the first show(), so no frame paints them stacked.
      this.dataset.ready = 'true';
      this.show(this.index);
      this.startTimer();
    }

    startTimer() {
      this.stopTimer();
      if (!this.dataset.ready || this.hovered || this.focused || document.hidden) return;
      this.timer = window.setInterval(() => {
        this.show((this.index + 1) % this.slides.length);
      }, this.interval);
    }

    stopTimer() {
      if (this.timer) window.clearInterval(this.timer);
      this.timer = null;
    }

    show(index) {
      this.index = index;
      this.all.forEach((slide) => {
        const current = slide === this.slides[index];
        slide.classList.toggle('is-current', current);
        // A link in a hidden announcement must not be reachable by Tab.
        slide.toggleAttribute('inert', !current);
      });
    }
  }

  if (!customElements.get('announcement-rotator')) {
    customElements.define('announcement-rotator', AnnouncementRotator);
  }
})();
